import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdTypeInfoKey } from './hd-chart-basics-info-metadata';

const HD_CHART_BASICS_ASSET_BASE = '/assets/docs/human-design';
const HD_CHART_BASICS_MARKDOWN_LANGUAGES = new Set(['nl', 'en']);
const PROPERTIES_PREFIX = 'humanDesign.properties';

/** Documentmappen van de info-panelen onder `docs/human-design/` (`cosmology` = de drie dragers van bewustzijn). */
export type HdChartBasicsInfoFolder = 'basics' | 'types' | 'authorities' | 'definitions' | 'cosmology';

/**
 * Gedeelde toegang van de Basics-, Type-, Autoriteit- en Definitie-panelen:
 * markdown-urls per contenttaal (patroon {@link HdProfileInfoService}),
 * scope-teksten (`human-design/chart-basics`) en de root-eigenschapslabels
 * (`humanDesign.properties.*`, bv. de type- en autoriteitsnamen).
 */
@Injectable({ providedIn: 'root' })
export class HdChartBasicsInfoService {
  private readonly contentLanguage = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly transloco = inject(TranslocoService);

  /** `file` is een pad zonder extensie binnen de map, bv. `'general'` of `'generator/strategy'`. */
  markdownUrl(folder: HdChartBasicsInfoFolder, file: string, lang?: string): string {
    return `${HD_CHART_BASICS_ASSET_BASE}/${folder}/${this.resolveMarkdownLanguage(lang)}/${file}.md`;
  }

  /** Scope-tekst in de gegeven taal; `''` wanneer afwezig. */
  text(key: string, lang: string): string {
    return this.gateInfo.chartBasicsInfoTextIn(key, lang);
  }

  /** Root-eigenschapslabel (`humanDesign.properties.<key>`) in de contenttaal; `''` wanneer de key ontbreekt. */
  propertyText(key: string, lang: string): string {
    const path = `${PROPERTIES_PREFIX}.${key}`;
    const translated = this.transloco.translate(path, undefined, lang);
    return translated === path ? '' : translated;
  }

  /** Typelabel zoals het Basis-paneel het toont ("Manifesterende Generator"). */
  typeLabel(typeKey: HdTypeInfoKey, lang: string): string {
    if (typeKey === 'manifesting-generator') {
      return `${this.propertyText('typeValues.manifesting', lang)} ${this.propertyText('typeValues.generator', lang)}`;
    }
    return this.propertyText(`typeValues.${typeKey}`, lang);
  }

  private resolveMarkdownLanguage(lang?: string): string {
    const requestedLanguage = lang ?? this.contentLanguage.effectiveLang();
    const normalizedLanguage = (requestedLanguage || '').toLowerCase().split('-')[0];
    return HD_CHART_BASICS_MARKDOWN_LANGUAGES.has(normalizedLanguage) ? normalizedLanguage : 'nl';
  }
}
